import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { routeTree } from './routeTree.gen';
import { ConfigProvider } from './config';
import { applicationConfig } from './config/application';
import { deLocalizeUrl, localizeUrl } from './paraglide/runtime.js';
import './index.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: applicationConfig.ENVIRONMENT !== 'local',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
  rewrite: {
    input: ({ url }) => deLocalizeUrl(url),
    output: ({ url }) => localizeUrl(url),
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth0();
  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <Auth0Provider
      domain={applicationConfig.AUTH0_DOMAIN}
      clientId={applicationConfig.AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: applicationConfig.AUTH0_AUDIENCE,
      }}
      cacheLocation={
        applicationConfig.ENVIRONMENT === 'local' ? 'localstorage' : 'memory'
      }
      onRedirectCallback={appState => {
        router.navigate({
          to: appState?.returnTo ?? '/dashboard',
          replace: true,
        });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ConfigProvider>
          <InnerApp />
          {applicationConfig.ENVIRONMENT === 'local' && <ReactQueryDevtools />}
        </ConfigProvider>
      </QueryClientProvider>
    </Auth0Provider>
  );
}

const rootElement = document.getElementById('root')!;
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
